import { Children, isValidElement } from 'react'
import {
  type AutocompleteProps,
  type InputAdornmentProps,
  type IconButtonProps,
  type TextFieldProps,
} from '@mui/material'
import { prepareForMui9PropPropagation, type CompatibleRenderInputParams } from '../helpers'
import { type DataTestIdAttribute } from '../../../types'
import { Progress } from '../../../primitives/Progress'
import { TextField } from '../../TextField'

type SlotProps = Partial<Pick<NonNullable<TextFieldProps['slotProps']>, 'htmlInput' | 'input'>>

export type RenderInputProps<
  Value,
  Multiple extends boolean,
  DisableClearable extends boolean,
  FreeSolo extends boolean,
> = Pick<TextFieldProps, 'placeholder' | 'label' | 'required' | 'helperText' | 'error' | 'name'> &
  Pick<AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>, 'size' | 'loading'> &
  DataTestIdAttribute & {
    slotProps?: SlotProps
  }

export const renderInput = <
  Value,
  Multiple extends boolean,
  DisableClearable extends boolean,
  FreeSolo extends boolean,
>({
  size,
  loading,
  required,
  placeholder,
  'data-testid': dataTestId,
  label,
  name,
  helperText,
  error,
  slotProps: inputSlotProps,
}: RenderInputProps<Value, Multiple, DisableClearable, FreeSolo>) =>
  function render(inputParams: CompatibleRenderInputParams) {
    /**
     * Handle both MUI7 and MUI9 structures
     */
    const slotProps = prepareForMui9PropPropagation(inputParams)
    /**
     * problem: the endAdornment already gets the `MuiAutocomplete-endAdornment` wrapper,
     * in addition to the `endAdornment` marker in the text input
     * so, prepending the Progress will not place it within the endAdornment
     * so either we need to inject the Progress in the children there
     * or, as done now, extract the children and dump those in our container
     */
    const endAdornmentChildren = isValidElement<InputAdornmentProps>(slotProps?.input?.endAdornment)
      ? Children.toArray(slotProps.input.endAdornment.props.children).filter(isValidElement<IconButtonProps>)
      : null

    return (
      <TextField
        placeholder={placeholder}
        {...inputParams}
        name={name}
        required={required}
        size={size}
        helperText={helperText}
        error={error}
        {...(size === 'small' ? {} : { label })}
        slotProps={{
          htmlInput: {
            ...slotProps.htmlInput,
            ...inputSlotProps?.htmlInput,
            'data-testid': `${dataTestId}-input`,
          },
          input: {
            ...slotProps.input,
            ...inputSlotProps?.input,
            endAdornment: (
              <div className="MuiAutocomplete-endAdornment">
                {loading ? <Progress color="inherit" sx={{ mr: 0.5 }} /> : null}
                {endAdornmentChildren}
              </div>
            ),
          },
        }}
      />
    )
  }
